import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';

@Injectable({
  providedIn: 'root',
})
export class Auth {
  private _session = signal<Session | null>(null);

  session = this._session.asReadonly();

  private router = inject(Router);

  /**
   * Resolves once the initial session has been fetched from Supabase.
   * The auth guard awaits this before checking isAuthenticated, which
   * prevents the race condition that logs users out on page refresh.
   */
  readonly initialized: Promise<void>;

  constructor() {
    this.initialized = this.initialize();
  }

  private async initialize(): Promise<void> {
    const { data } = await supabase.auth.getSession();
    this._session.set(data.session);

    supabase.auth.onAuthStateChange((_event, session) => {
      this._session.set(session);
    });
  }

  async login(email: string, password: string): Promise<void> {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
  }

  async resetPassword(email: string): Promise<void> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/update-password`,
    });

    if (error) throw error;
  }

  async logout(): Promise<void> {
    await supabase.auth.signOut();
    this._session.set(null);
    this.router.navigate(['/login']);
  }

  get accessToken(): string | null {
    return this._session()?.access_token ?? null;
  }

  get isAuthenticated(): boolean {
    return !!this._session();
  }
}
